import { compact } from 'lodash';
import { applyMiddleware, createStore } from 'redux';
import thunk from 'redux-thunk';
import {
  FETCH_BLOG_DATA_SUCCESS,
  GLOBALS,
  SET_APP_CONFIG,
} from '@wix/communities-blog-client-common';

import { type PlatformApi } from '@app/external/common/controller/platform-api';
import { type Router } from '@app/external/common/router';
import { setInstanceValues } from '@app/external/common/store/instance-values/instance-values-actions';
import { type AppStore, type ControllerConfig, type FlowAPI } from '@app/external/common/types';
import { appUouBiMiddleware, appUsersBiMiddleware } from '../../common/bi-events/init-middleware';
import { getInstance } from '../../common/controller/helpers';
import { createMessageMiddleware } from '../../common/messages/framework/initialize-store';
import createRouterMiddleware from '../../common/middleware/create-router-middleware';
import {
  createDuplexerMiddleware,
  socketEventHandlers,
} from '../../common/middleware/duplexer-middleware';
import { postMetadataMiddleware } from '../../common/middleware/post-metadata-middleware';
import { debounceActions, throttleActions } from '../../common/middleware/redux-middlewares';
import { createModalOpenedMiddleware } from '../../common/modals/framework/initialize-store';
import { SET_APP_SETTINGS } from '../../common/store/app-settings/app-settings-actions';
import { SET_BASIC_PARAMS } from '../../common/store/basic-params/basic-params-actions';
import { FETCH_CATEGORIES_SUCCESS } from '../../common/store/categories/fetch-categories';
import { FETCH_SITE_PROPERTIES_SUCCESS } from '../../common/store/site-properties/site-properties-types';
import { FETCH_TOPOLOGY_SUCCESS } from '../../common/store/topology/topology-actions';
import { waitForActionMiddleware } from '../../common/store/wait-for-action/wait-for-action-middleware';
import { eventMap } from '../bi-events/uou-events';
import { usersEventMap } from '../bi-events/users-events';
import messageResolvers from '../components/messages/message-resolvers';
import { modalDataLoaderByType } from '../components/modals/modal-data-loader-map-by-type';
import reducers from '../reducers';
import { type FeedPageThunkArgs } from '../types/feed-page-thunk-action.type';

export function createReduxStore({
  appParams,
  platformApi,
  compId,
  platformAPIs,
  getRouter,
  isSSR,
  isEditor,
  isPreview,
  language,
  flowAPI,
}: {
  appParams: ControllerConfig['appParams'];
  platformApi: PlatformApi;
  compId: ControllerConfig['compId'];
  platformAPIs: ControllerConfig['platformAPIs'];
  getRouter: () => Router;
  isSSR: boolean;
  isEditor: boolean;
  isPreview: boolean;
  language: string;
  bundleName: string;
  flowAPI: FlowAPI;
}) {
  const disableDuplexerByInstanceId = (GLOBALS.DISABLE_DUPLEXER_FOR_INSTANCE_IDS || '')
    .split(',')
    .includes(appParams.instanceId);
  const useDuplexer = !isSSR && !isEditor && !isPreview && !disableDuplexerByInstanceId;

  const middleware = compact([
    throttleActions(),
    debounceActions(),
    thunk.withExtraArgument({
      platformApi,
      compId,
      appParams,
      panoramaClient: flowAPI.panoramaClient,
      getRouter,
      httpClient: flowAPI.httpClient,
      errorHandler: flowAPI.errorHandler,
      flowAPI,
    } satisfies FeedPageThunkArgs),
    waitForActionMiddleware([
      SET_APP_SETTINGS,
      SET_APP_CONFIG,
      setInstanceValues.type,
      SET_BASIC_PARAMS,
      FETCH_BLOG_DATA_SUCCESS,
      FETCH_SITE_PROPERTIES_SUCCESS,
      FETCH_TOPOLOGY_SUCCESS,
      FETCH_CATEGORIES_SUCCESS,
    ]),

    (isEditor || isPreview) && createRouterMiddleware(getRouter),
    createModalOpenedMiddleware({ modalDataLoaderByType }),
    createMessageMiddleware({ resolvers: messageResolvers }),
    postMetadataMiddleware({ isSSR, language }),
    isEditor
      ? appUsersBiMiddleware({
          platformAPIs,
          instanceId: appParams.instanceId,
          eventMap: usersEventMap,
        })
      : appUouBiMiddleware({
          platformAPIs,
          instanceId: appParams.instanceId,
          eventMap,
        }),
    useDuplexer &&
      createDuplexerMiddleware({
        getInstance: getInstance(platformApi),
        eventHandlers: socketEventHandlers,
        instanceId: appParams.instanceId,
        captureMessage: (message: string, extra: Record<string, any>) => {
          flowAPI.panoramaClient.errorMonitor().reportError(new Error(message), extra);
        },
      }),
  ]);

  const initialState = { experiments: flowAPI.essentials.experiments.all() };

  return createStore(reducers, initialState, applyMiddleware(...middleware)) as unknown as AppStore;
}
